// src/components/Header.jsx
import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuStructure } from '../data/menuStructure';
import "./Header.css";
import { useAppContext } from "../libs/contextLib";

const Header = () => {
  const [showDropdown, setShowDropdown] = useState({});
  const [subMenuPositions, setSubMenuPositions] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRefs = useRef({});
  const { isAuthenticated, userHasAuthenticated } = useAppContext();
  const { user, setUser } = useAppContext();

  // 현재 경로가 해당 메뉴 경로와 일치하는지 확인
  const isCurrentPath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // 메뉴 호버 시작
  const handleMouseEnter = (menuKey) => {
    // 기존 타이머 제거
    if (timeoutRefs.current[menuKey]) {
      clearTimeout(timeoutRefs.current[menuKey]);
    }
    
    const menu = menuStructure[menuKey];
    if (menu.submenus && menu.submenus.length > 0) {
      setShowDropdown(prev => ({ ...prev, [menuKey]: true }));
    }
  };

  // 메뉴 호버 종료
  const handleMouseLeave = (menuKey) => {
    timeoutRefs.current[menuKey] = setTimeout(() => {
      setShowDropdown(prev => ({ ...prev, [menuKey]: false }));
      setSubMenuPositions(prev => {
        const newPositions = { ...prev };
        Object.keys(newPositions).forEach(key => {
          if (key.startsWith(menuKey)) {
            delete newPositions[key];
          }
        });
        return newPositions;
      });
    }, 150);
  };

  // 서브메뉴 호버 시작
  const handleSubMenuMouseEnter = (menuKey, submenuId, event) => {
    // 기존 타이머 제거
    Object.keys(timeoutRefs.current).forEach(key => {
      if (timeoutRefs.current[key]) {
        clearTimeout(timeoutRefs.current[key]);
      }
    });

    const rect = event.currentTarget.getBoundingClientRect();
    const positionKey = `${menuKey}-${submenuId}`;
    
    setSubMenuPositions(prev => ({
      ...prev,
      [positionKey]: {
        top: rect.top,
        left: rect.right + 5
      }
    }));
  };

  // 서브메뉴 호버 종료
  const handleSubMenuMouseLeave = (menuKey, submenuId) => {
    const positionKey = `${menuKey}-${submenuId}`;
    timeoutRefs.current[positionKey] = setTimeout(() => {
      setSubMenuPositions(prev => {
        const newPositions = { ...prev };
        delete newPositions[positionKey];
        return newPositions;
      });
    }, 150);
  };

  // 메뉴 클릭 핸들러
  const handleMenuClick = (path) => {
    if ( path === "/login" ) {
      // logout 처리함.
      // add sign out login...
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('access_user');
      userHasAuthenticated(false);
      setUser(null);
    }

    if (isAuthenticated) {
      navigate(path);
      // 드롭다운 닫기
      setShowDropdown({});
      setSubMenuPositions({});
    } else {
      navigate("/login");
    }
  
  };

  // 서브메뉴 렌더링
  const renderSubMenu = (menuKey, submenu) => {
    const hasSubSubMenus = submenu.submenus && submenu.submenus.length > 0;
    const positionKey = `${menuKey}-${submenu.id}`;

    return (
      <div key={submenu.id} className="dropdown-item-container">
        <div
          className={`dropdown-item ${isCurrentPath(submenu.path) ? 'active' : ''}`}
          onClick={() => handleMenuClick(submenu.path)}
          onMouseEnter={(e) => hasSubSubMenus && handleSubMenuMouseEnter(menuKey, submenu.id, e)}
          onMouseLeave={() => hasSubSubMenus && handleSubMenuMouseLeave(menuKey, submenu.id)}
        >
          <span className="menu-text">{submenu.name}</span>
          {hasSubSubMenus && <span className="arrow-right">▶</span>}
        </div>
        
        {/* 3단계 서브메뉴 */}
        {hasSubSubMenus && subMenuPositions[positionKey] && (
          <div 
            className="sub-dropdown-menu"
            style={{
              position: 'fixed',
              top: subMenuPositions[positionKey].top,
              left: subMenuPositions[positionKey].left,
              zIndex: 1002
            }}
            onMouseEnter={(e) => handleSubMenuMouseEnter(menuKey, submenu.id, e)}
            onMouseLeave={() => handleSubMenuMouseLeave(menuKey, submenu.id)}
          >
            {submenu.submenus.map((subSubmenu) => (
              <div
                key={subSubmenu.id}
                className={`dropdown-item ${isCurrentPath(subSubmenu.path) ? 'active' : ''}`}
                onClick={() => handleMenuClick(subSubmenu.path)}
              >
                <span className="menu-text">{subSubmenu.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="app-header">
      <div className="header-container">
        {/* 로고 및 제목 */}
        <div className="header-left">
          <div className="logo" onClick={() => handleMenuClick('/')}>
            <div className="logo-icon">🛡️</div>
          </div>
          <h1 className="system-title">
                STEP-IS  
                <span style={{ display: 'block', fontSize: '1rem', fontWeight: 'normal' }}>
                주행상황 인지SW 평가플랫폼
                </span>
            </h1>
        </div>
        {/* 메인 네비게이션 메뉴 */}
        <nav className="main-navigation">
          {Object.entries(menuStructure).map(([menuKey, menu]) => (
            <div 
              key={menuKey}
              className="nav-item-container"
              onMouseEnter={() => handleMouseEnter(menuKey)}
              onMouseLeave={() => handleMouseLeave(menuKey)}
            >
              <button
                className={`nav-item ${isCurrentPath(menu.path) ? 'active' : ''}`}
                onClick={() => handleMenuClick(menu.path)}
              >
                <span className="nav-text">{menu.name}</span>
                {menu.submenus && menu.submenus.length > 0 && (
                  <span className="dropdown-indicator">▼</span>
                )}
              </button>

              {/* 드롭다운 메뉴 */}
              {showDropdown[menuKey] && menu.submenus && menu.submenus.length > 0 && (
                <div className="dropdown-menu">
                  <div className="dropdown-content">
                    {menu.submenus.map(submenu => renderSubMenu(menuKey, submenu))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* 헤더 우측 영역 (사용자 정보, 설정 등) */}
        <div className="header-right">
          <div className="user-menu">
            {/*
            <button className="notification-btn" title="알림">
              🔔
              <span className="notification-badge">3</span>
            </button>
            <button className="settings-btn" title="설정">
              ⚙️
            </button>
            */}
            <div 
              className="user-profile"
              onClick={() => handleMenuClick("/login")}
            >
              <img src="/api/placeholder/32/32" alt="User" className="user-avatar" />
              <span className="user-name">{
                  (isAuthenticated && user) ? user.nickname : 'GUEST'
                  } 님</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;